import {RestAdministration} from "@softvision/webpdf-wsclient-9-typescript/lib/session/rest/RestAdministration";
import {
	Application,
	ApplicationInterface,
	ConfigurationResult,
	ConfigurationType,
	ExecutableName,
	LogFileConfigurationInterface,
	ServerInterface,
	ServerStatusInterface,
	SessionTableInterface,
	UserCredentials,
	UsersInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import AdminSession from "./AdminSession";
import AdminContext from "../AdminContext";
import Exception from "../../shared/exception/Exception";
import {HttpHeaders, HttpMethod} from "@softvision/webpdf-wsclient-9-typescript";
import {
	AbstractRestAdministration
} from "@softvision/webpdf-wsclient-9-typescript/lib/session/rest/AbstractRestAdministration";
import {HttpRestResponse} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestResponse";
import {AdminConfig, AdminConfigCheckTypes} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Administration";
import i18next from "i18next";
import Immutable from "../../shared/immutable/Immutable";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {HttpRestRequestFactory} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestRequestFactory";

export interface AdministrationGlobals {
	sessionTable?: Immutable<SessionTableInterface>
	serverStatus?: Immutable<ServerStatusInterface>,
	applicationConfig?: Immutable<ApplicationInterface>,
	serverConfig?: Immutable<ServerInterface>,
	userConfig?: Immutable<UsersInterface>,
	logConfig?: Immutable<LogFileConfigurationInterface>
}

export default class Administration extends RestAdministration {
	private _context: AdminContext;

	constructor(session: AdminSession) {
		super(session);

		this._context = new AdminContext();
	}

	public async initialize(): Promise<void> {
		if (!FunctionHelper.applyValue(this._context.user?.isAdmin, UserCredentials.getIsAdminDefault())) {
			return;
		}

		await this.updateServerStatus();
		await this.fetchConfiguration(ConfigurationType.Application);
		await this.fetchConfiguration(ConfigurationType.Server);
		await this.fetchConfiguration(ConfigurationType.User);
		await this.fetchConfiguration(ConfigurationType.Log);
	}

	public async updateConfiguration(
		type: ConfigurationType, config: AdminConfig, checks?: Array<AdminConfigCheckTypes>
	): Promise<ConfigurationResult> {
		let result: ConfigurationResult = await super.updateConfiguration(type, config, checks);

		if (FunctionHelper.exists(result)) {
			if (result.error?.code !== 0 || result.error?.exitCode !== 0) {
				throw Exception.parseAdminResult(result);
			}
		}

		await this.fetchConfiguration(type);

		return result;
	}

	public async fetchConfiguration(type: ConfigurationType): Promise<AdminConfig> {
		let configuration: AdminConfig = await super.fetchConfiguration(type);

		switch (type) {
			case ConfigurationType.Application:
				this._context.getPage().setState({
					applicationConfig: Immutable.from(configuration.toJson())
				});
				break;
			case ConfigurationType.Server:
				this._context.getPage().setState({
					serverConfig: Immutable.from(configuration.toJson())
				});
				break;
			case ConfigurationType.User:
				this._context.getPage().setState({
					userConfig: Immutable.from(configuration.toJson())
				});
				break;
			case ConfigurationType.Log:
				this._context.getPage().setState({
					logConfig: Immutable.from(configuration.toJson())
				});
				break;
		}

		return configuration;
	}

	public async validateConfiguration(
		type: ConfigurationType, config: AdminConfig, checks: Array<AdminConfigCheckTypes>
	): Promise<ConfigurationResult> {
		let result: ConfigurationResult = await super.validateConfiguration(type, config, checks);

		if (FunctionHelper.exists(result)) {
			if (result.error?.code !== 0 || result.error?.exitCode !== 0) {
				throw Exception.parseAdminResult(result);
			}
		}

		return result;
	}

	public async testExecutables(
		config: Application, executables: Array<ExecutableName>
	): Promise<ConfigurationResult> {
		let result: ConfigurationResult = await super.testExecutables(config, executables);

		if (FunctionHelper.exists(result)) {
			if (result.error?.code !== 0 || result.error?.exitCode !== 0) {
				throw Exception.parseAdminResult(result);
			}
		}

		return result;
	}

	public async updateServerStatus(): Promise<void> {
		this._context.getPage().setState({
			serverStatus: Immutable.from(await this.getStatus())
		});
	}

	public async updateSessionTable(): Promise<void> {
		this._context.getPage().setState({
			sessionTable: Immutable.from(await this.getSessionTable())
		});
	}

	public set context(value: AdminContext) {
		this._context = value;
	}

	public async getLogLength(date?: string): Promise<number> {
		this.validateUser();

		let searchParams: URLSearchParams = new URLSearchParams();
		if (FunctionHelper.exists(date)) {
			searchParams.set("date", date!);
		}

		let headers: Headers = new Headers();
		headers.append(HttpHeaders.ACCEPT, "*/*");
		headers.append("Accept-Language", i18next.language);

		let response: HttpRestResponse = await HttpRestRequestFactory.createInstance(this.session)
		.buildApiRequest({
			httpMethod: HttpMethod.HEAD,
			path: AbstractRestAdministration.LOG_PATH + (FunctionHelper.exists(date) ? "?" + searchParams : ""),
			additionalHeaders: headers
		})
		.execute();

		let responseHeader: any = response.getHeaders();
		return FunctionHelper.applyValue(responseHeader["content-length"], 0);
	}

	public async getLog(range?: string, date?: string): Promise<string> {
		this.validateUser();

		let searchParams: URLSearchParams = new URLSearchParams();
		if (FunctionHelper.exists(date)) {
			searchParams.set("date", date!);
		}

		let headers: Headers = new Headers();
		headers.append(HttpHeaders.ACCEPT, "*/*");
		headers.append("range", "bytes=" + (range || "0-"));
		headers.append("Accept-Language", i18next.language);

		let result: HttpRestResponse = await HttpRestRequestFactory.createInstance(this.session)
		.buildApiRequest({
			httpMethod: HttpMethod.GET,
			path: AbstractRestAdministration.LOG_PATH + "?" + searchParams,
			additionalHeaders: headers
		})
		.execute();

		return await result.getData() as string;
	}

	static readonly STATISTICS_PATH: string = "admin/statistic/realtime/{range}";
}